import {
  type AltchaWidgetElement,
  makeAltchaBoxClickable,
  setupAltcha,
  waitForAltcha,
} from "../../utils/altcha";
import {
  type CodeInputController,
  type OtpErrorMessages,
  requestLoginCode,
  setupCodeInput,
  verifyLoginCode,
} from "../../utils/otp";

setupAltcha();

export const initAuth = () => {
  const auth = document.getElementById("auth") as HTMLDivElement;

  if (!auth) {
    return;
  }

  const stepSocial = auth.querySelector(".step-social") as HTMLDivElement;
  const stepEmail = auth.querySelector(".step-email") as HTMLDivElement;
  const stepCode = auth.querySelector(".step-code") as HTMLDivElement;

  const emailForm = stepEmail.querySelector(".email-form") as HTMLFormElement;

  const csrfRequestCode = (auth.querySelector(".csrf-request-code") as HTMLInputElement).value;
  const csrfVerifyCode = (auth.querySelector(".csrf-verify-code") as HTMLInputElement).value;

  // Mobile-only header sections (SVG waves + reviews) — hide on email/OTP steps
  const authWrapper = document.getElementById("auth-wrapper");
  function hideMobileHeaders() {
    authWrapper?.querySelectorAll<HTMLElement>(".mobile-header").forEach((el) => {
      el.classList.add("d-none");
    });
  }

  // Show email step when clicking "Continue with email"
  const showEmailButton = stepSocial.querySelector(".show-email-form") as HTMLButtonElement;
  showEmailButton.addEventListener("click", () => {
    stepSocial.classList.add("d-none");
    stepEmail.classList.remove("d-none");
    hideMobileHeaders();

    const emailInput = emailForm.querySelector(".email") as HTMLInputElement;
    emailInput.focus();
  });

  // Back button from code step
  const backButton = stepCode.querySelector(".back") as HTMLButtonElement;
  backButton.addEventListener("click", () => {
    stepCode.classList.add("d-none");
    stepEmail.classList.remove("d-none");
  });

  let currentEmail = "";

  const continueButton = stepEmail.querySelector(".continue") as HTMLButtonElement;
  const continueSpinner = continueButton.querySelector(".spinner") as HTMLSpanElement;

  const altchaWrapper = stepEmail.querySelector(".altcha-wrapper") as HTMLDivElement;
  const altchaWidget = altchaWrapper.querySelector("altcha-widget") as AltchaWidgetElement;
  makeAltchaBoxClickable(altchaWidget);

  continueButton.addEventListener("click", (e) => {
    e.preventDefault();

    continueSpinner.classList.remove("d-none");
    continueButton.disabled = true;

    waitForAltcha(altchaWrapper, altchaWidget)
      .then((payload) => {
        if (!emailForm.checkValidity()) {
          emailForm.reportValidity();
          resetContinueButton();
          return;
        }

        const emailInput = stepEmail.querySelector(".email") as HTMLInputElement;
        currentEmail = emailInput.value;
        sendLoginCodeRequest(payload);
      })
      .catch(() => {
        resetContinueButton();
        alert(errorMessages.generic);
      });
  });

  function resetContinueButton() {
    continueSpinner.classList.add("d-none");
    continueButton.disabled = false;
  }

  function getSpamFields() {
    const spamvalidation = stepEmail.querySelector(
      'input[name="spamvalidation"]',
    ) as HTMLInputElement | null;
    const yourLastName = stepEmail.querySelector(
      'input[name="your_last_name"]',
    ) as HTMLInputElement | null;
    return {
      spamvalidation: spamvalidation?.value || "",
      yourLastName: yourLastName?.value || "",
    };
  }

  function sendLoginCodeRequest(altchaPayload: string) {
    requestLoginCode(currentEmail, csrfRequestCode, altchaPayload, getSpamFields(), errorMessages)
      .then((result) => {
        resetContinueButton();

        if (!result.success) {
          alert(result.errorMessage);
          return;
        }

        showCodeStep();
      })
      .catch(() => {
        resetContinueButton();
      });
  }

  function showCodeStep() {
    stepEmail.classList.add("d-none");
    stepCode.classList.remove("d-none");

    // Show email in description
    const codeDescription = stepCode.querySelector(".code-description") as HTMLParagraphElement;
    codeDescription.textContent = codeDescription.textContent
      ? codeDescription.textContent.replace(/\S+@\S+|(?=\.)/, currentEmail)
      : `We sent a code to ${currentEmail}.`;

    resetCodeInput();
  }

  // Code input
  const codeInputWrapper = stepCode.querySelector(".code-input-wrapper") as HTMLDivElement;
  const codeSpinner = stepCode.querySelector(".code-spinner") as HTMLDivElement;
  const codeError = stepCode.querySelector(".code-error") as HTMLDivElement;
  const codeErrorMessage = stepCode.querySelector(".code-error-message") as HTMLDivElement;
  const errorMessages: OtpErrorMessages = {
    generic: stepCode.dataset.genericError || "Something went wrong.",
    rateLimited: stepCode.dataset.rateLimitError || "Too many requests.",
  };
  let isVerifying = false;

  const codeInput: CodeInputController = setupCodeInput(
    codeInputWrapper,
    (code) => {
      if (!isVerifying) {
        submitCode(code);
      }
    },
    () => {
      codeError.classList.add("d-none");
    },
  );

  function submitCode(code: string) {
    isVerifying = true;
    codeInput.disable();
    codeSpinner.classList.remove("d-none");
    codeError.classList.add("d-none");

    verifyLoginCode(currentEmail, code, csrfVerifyCode, errorMessages)
      .then((result) => {
        if (result.success && result.redirect) {
          window.location.href = result.redirect;
          return;
        }

        codeErrorMessage.textContent = result.errorMessage || errorMessages.generic;
        codeError.classList.remove("d-none");
        resetCodeInput();
      })
      .catch(() => {
        codeErrorMessage.textContent = errorMessages.generic;
        codeError.classList.remove("d-none");
        resetCodeInput();
      });
  }

  function resetCodeInput() {
    isVerifying = false;
    codeSpinner.classList.add("d-none");
    codeInput.reset();
  }

  // Resend code
  const resendCodeButton = stepCode.querySelector(".resend-code") as HTMLButtonElement;
  const resendAltchaWrapper = stepCode.querySelector(".resend-altcha-wrapper") as HTMLDivElement;
  const resendAltchaWidget = resendAltchaWrapper.querySelector(
    "altcha-widget",
  ) as AltchaWidgetElement;
  makeAltchaBoxClickable(resendAltchaWidget);

  resendCodeButton.addEventListener("click", () => {
    resendCodeButton.disabled = true;

    waitForAltcha(resendAltchaWrapper, resendAltchaWidget)
      .then((payload) => sendResendCodeRequest(payload))
      .catch(() => {
        resendCodeButton.disabled = false;
        alert(errorMessages.generic);
      });
  });

  function sendResendCodeRequest(altchaPayload: string) {
    requestLoginCode(currentEmail, csrfRequestCode, altchaPayload, getSpamFields(), errorMessages)
      .then((result) => {
        resendCodeButton.disabled = false;

        if (!result.success) {
          alert(result.errorMessage);
          return;
        }

        alert(resendCodeButton.dataset.successMessage || "Code sent!");
      })
      .catch(() => {
        resendCodeButton.disabled = false;
      });
  }
};
